import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from 'use-debounce';

/**
 * useSearch
 *
 * Debounces a search string and converts it into the filter array shape
 * the GraphQL query expects.
 *
 * Returns [search, setSearch] — identical external API to before.
 */
export const useSearch = (initialState = []) => {
  const [rawSearch, setSearch] = useState('');
  const [searchKey] = useDebounce(rawSearch, 1000);

  const searchFilters = useMemo(() => {
    if (searchKey && searchKey !== '') {
      return [{ field: 'search.q', value: searchKey }];
    }
    return initialState;
  }, [searchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return [searchFilters, setSearch];
};

/**
 * useSort
 *
 * Converts a { sortField, sortOrder } object from the Table component into
 * the filter array shape the GraphQL query expects.
 *
 */
export const useSort = (initialState = [{
  field: 'options.sort_fields',
  value: 'modified_at.dsc',  
}]) => {
  const [sortKey, setSort] = useState(null);

  const sortFilters = useMemo(() => {
    if (sortKey) {
      return [{
        field: 'options.sort_fields',
        value: `${sortKey.sortField}.${sortKey.sortOrder === 'desc' ? 'dsc' : sortKey.sortOrder}`,
      }];
    }
    return initialState;
  }, [sortKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return [sortFilters, setSort];
};

/**
 * useFilter
 *
 * Maps a filter selection object into and/or filter arrays for GraphQL.
 */
export const useFilter = (initialState = []) => {
  const [filterKey, setFilter] = useState(null);

  const { filter, orFilter } = useMemo(() => {
    if (!filterKey) return { filter: initialState, orFilter: initialState };

    const trigger = filterKey?.filter_one ?? 'all';

    switch (trigger) {
      case 'all':
        return { filter: initialState, orFilter: initialState };

      case 'policy_violations':
      case 'firmware':
      case 'model':
      case 'policy':
        try {
          return { filter: initialState, orFilter: JSON.parse(filterKey.filter_two) };
        } catch {
          return { filter: initialState, orFilter: initialState };
        }

      default:
        try {
          return { filter: [JSON.parse(filterKey.filter_one)], orFilter: initialState };
        } catch {
          return { filter: initialState, orFilter: initialState };
        }
    }
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return [filter, setFilter, orFilter];
};
